import { useCallback, useEffect, useState } from 'react';
import { messageForError } from '@/lib/api';
import { DEMO_ASSISTANT_GREETING } from '@/mocks/fixtures/assistant';
import { askAssistant } from '../api/ask-assistant';

export interface ChatMessage {
  id: string;
  from: 'ai' | 'user';
  text: string;
  link?: { label: string; to: string };
}

const greeting = (): ChatMessage => ({
  id: 'msg_greeting',
  from: 'ai',
  text: DEMO_ASSISTANT_GREETING,
});

/**
 * Conversation state for the assistant drawer.
 *
 * The thread is intentionally per-session: KainOS answers about live project
 * state, so a stale transcript would be worse than starting fresh.
 */
export function useAiChat(seed: string | null) {
  const [messages, setMessages] = useState<ChatMessage[]>(() => [greeting()]);
  const [isPending, setIsPending] = useState(false);

  const ask = useCallback(async (question: string) => {
    const trimmed = question.trim();
    if (!trimmed) return;

    setMessages((current) => [
      ...current,
      { id: `msg_${crypto.randomUUID()}`, from: 'user', text: trimmed },
    ]);
    setIsPending(true);

    try {
      const result = await askAssistant(trimmed);
      setMessages((current) => [
        ...current,
        { id: `msg_${crypto.randomUUID()}`, from: 'ai', text: result.answer, link: result.link },
      ]);
    } catch (error) {
      setMessages((current) => [
        ...current,
        { id: `msg_${crypto.randomUUID()}`, from: 'ai', text: messageForError(error) },
      ]);
    } finally {
      setIsPending(false);
    }
  }, []);

  // A drawer opened from a specific card arrives with that question queued.
  useEffect(() => {
    if (seed) void ask(seed);
  }, [seed, ask]);

  const reset = useCallback(() => setMessages([greeting()]), []);

  return { messages, isPending, ask, reset };
}
